import { useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import InviteUsersSelect from "frontend/ui-components/form-fields/form-bubbles-field/invite-users-select/invite-users-select";
import {
  useInviteUsersSelect,
  useInviteUsersSelectState,
} from "frontend/ui-components/form-fields/form-bubbles-field/invite-users-select/hooks";
import { BubbleFieldMutation } from "frontend/ui-components/form-fields/form-bubbles-field";
import EmailInput, { type EmailInputProps } from "./email-input";
import { useOnClickOutside } from "usehooks-ts";
import { useScrollAwareDropdown } from "./use-scroll-aware-dropdown";

// Mock handlers for the hooks
function handleBlur() {}
function handleFocus() {}
function handleInput() {}

export default function EmailInputWithDropdown({
  id,
  value,
  disabled,
  onChange,
  onKeyDown,
  isValidEmail,
}: EmailInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const usersDropdownRef = useRef<HTMLDivElement>(null);

  const existingValues = useMemo(() => new Set<string>([value]), [value]);

  const handleUserSelection = (mutation: BubbleFieldMutation) => {
    if (mutation.action === "add") {
      onChange(mutation.value);
    }
  };

  const { showDropdown, setShowDropdown, handleUserClick, handleCustomKeyDown, handleCustomFocus, selectedIndex } =
    useInviteUsersSelect(
      inputRef,
      usersDropdownRef,
      existingValues,
      handleUserSelection,
      handleBlur,
      handleFocus,
      handleInput
    );

  const { users, showInviteUsersSelect } = useInviteUsersSelectState(value, showDropdown, true);

  const { dropdownPosition, isVisible: isDropdownVisible } = useScrollAwareDropdown({
    elementId: id,
    isOpen: showDropdown,
    dropdownHeight: 300,
  });

  const handleUserClickWrapper = useCallback(
    (user: { email: string; thumbnail?: string; name?: string }) => {
      if (existingValues.has(user.email)) {
        setShowDropdown(false);
        return;
      }

      handleUserClick(user as any);
    },
    [handleUserClick, existingValues, setShowDropdown]
  );

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    // Show dropdown when user types
    setShowDropdown(true);
  };

  const handleCombinedKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Show dropdown on character input (not navigation/control keys)
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
      setShowDropdown(true);
    }

    // First try user select key handling
    handleCustomKeyDown(event, users);

    // If not handled by user select, use our email logic
    if (!event.defaultPrevented) {
      // Close dropdown when Enter, comma, or Tab is pressed (moving to next input)
      if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
        setShowDropdown(false);
      }
      onKeyDown(event);
    }
  };

  // Handle focus for EmailInput
  const handleEmailInputFocus = (_id: string) => {
    // Don't show dropdown on focus, only on user input
    handleCustomFocus({ currentTarget: { value: value } } as React.FocusEvent<HTMLInputElement>);
  };

  // Handle click to show dropdown
  const handleContainerClick = () => {
    setShowDropdown(true);
  };

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (usersDropdownRef?.current && usersDropdownRef.current.contains(e.target as Node)) {
        return;
      }
      setShowDropdown(false);
    },
    [usersDropdownRef, setShowDropdown]
  );

  useOnClickOutside(containerRef, handleClickOutside);

  return (
    <>
      <div ref={containerRef} onClick={handleContainerClick}>
        <EmailInput
          value={value}
          id={id}
          disabled={disabled}
          onChange={handleInputChange}
          onKeyDown={handleCombinedKeyDown}
          onFocus={handleEmailInputFocus}
          isValidEmail={isValidEmail}
        />
      </div>
      {showInviteUsersSelect &&
        isDropdownVisible &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: dropdownPosition.top + 5,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 10000,
            }}
          >
            <InviteUsersSelect
              rounded
              standaloneStyle
              users={users}
              usersDropdownRef={usersDropdownRef}
              inputValue={value}
              handleUserClick={handleUserClickWrapper}
              selectedIndex={selectedIndex}
            />
          </div>,
          document.body
        )}
    </>
  );
}
